import React, { memo, useRef, useState } from "react";
import { Box } from "@UI/Components/Box/Box";
import { CircleInfo } from "@UI/Components/Icons/CircleInfo";
import { Tooltip } from "@UI/Components/Tooltip/Tooltip";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import useThemeToken from "@UI/hooks/useThemeToken";
import { components } from "@api/schema";
import { IAppCurrency } from "@App/types";
import { useSubscriptionDiscountLabel } from "./useSubscriptionDiscountLabel";
import messages from "../messages";

export interface ISubscriptionDiscountProps {
  priceCents: number;
  rateType: components["schemas"]["serviceOffer"]["rateType"];
  billingUnit: components["schemas"]["serviceOffer"]["billingUnit"];
  currency: IAppCurrency;
  expensesFeesCents?: number;
  subscribed?: boolean;
  feePercentage?: number;
  w?: string;
}

export const SubscriptionDiscount = memo(function SubscriptionDiscount({
  priceCents,
  rateType,
  billingUnit,
  currency,
  expensesFeesCents,
  subscribed = false,
  feePercentage,
  w = "fit-content",
}: ISubscriptionDiscountProps) {
  const [isTooltipOpen, setIsTooltipOpen] = useState<boolean>(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const pink500 = useThemeToken("colors", "pink.500");
  const orange500 = useThemeToken("colors", "orange.500");
  const pink600 = useThemeToken("colors", "pink.600");
  const orange600 = useThemeToken("colors", "orange.600");

  const { amountLabel } = useSubscriptionDiscountLabel({
    priceCents,
    rateType,
    billingUnit,
    currency,
    expensesFeesCents,
    feePercentage,
  });

  type TPillKey =
    | "subscriptionDiscount.youSaved"
    | "subscriptionDiscount.saveWithPlus";
  type TTooltipKey =
    | "subscriptionDiscount.tooltip.subscribed"
    | "subscriptionDiscount.tooltip.notSubscribed";

  const pillMessageKey: TPillKey = subscribed
    ? "subscriptionDiscount.youSaved"
    : "subscriptionDiscount.saveWithPlus";

  const tooltipMessageKey: TTooltipKey = subscribed
    ? "subscriptionDiscount.tooltip.subscribed"
    : "subscriptionDiscount.tooltip.notSubscribed";

  return (
    <>
      <Tooltip
        label={
          <FormattedMessage
            {...messages[tooltipMessageKey]}
            values={{ amount: amountLabel }}
          />
        }
        textAlign="center"
        placement="auto"
        isOpen={isTooltipOpen}
        portalProps={{ containerRef: tooltipRef }}
      >
        <Box
          w={w}
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ padding: "0.75rem 12px" }}
          borderRadius="full"
          color="white"
          fontWeight={"normal"}
          fontSize="md"
          lineHeight="12px"
          cursor="pointer"
          background={`linear-gradient(96.51deg, ${pink500} 0%, ${orange500} 99.87%);`}
          _hover={{
            background: `linear-gradient(96.51deg, ${pink600} 0%, ${orange600} 99.87%);`,
          }}
          onClick={() => setIsTooltipOpen(!isTooltipOpen)}
          onMouseEnter={() => setIsTooltipOpen(true)}
          onMouseLeave={() => setIsTooltipOpen(false)}
        >
          <FormattedMessage
            {...messages[pillMessageKey]}
            values={{ amount: amountLabel }}
          />
          <CircleInfo
            style={{ marginLeft: "8px", color: "white", fontSize: "10px" }}
          />
        </Box>
      </Tooltip>
      <Box ref={tooltipRef} position="relative" zIndex={10001} />
    </>
  );
});
